import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/lib/api';
import { TOKEN_KEY } from '@/context/LanguageContext';
import { buildBase, getAt, setAt, overlay, LANG_KEYS } from '@/lib/content';

const Ctx = createContext(null);
export const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` } });
export const apiError = (e, fallback = 'حدث خطأ، حاول مرة أخرى') => {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((x) => x.msg || JSON.stringify(x)).join(' ');
  return fallback;
};

export function AdminContentProvider({ user, children }) {
  const [draft, setDraft] = useState(null);
  const [meta, setMeta] = useState({});
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [revisions, setRevisions] = useState([]);

  const loadRevisions = useCallback(async () => {
    const { data } = await axios.get(`${API}/admin/content/revisions`, authHeaders());
    setRevisions(data);
  }, []);

  useEffect(() => {
    axios.get(`${API}/content/draft`, authHeaders()).then(({ data }) => {
      const base = buildBase();
      const src = data.draft || data.published;
      setDraft(src ? Object.fromEntries(LANG_KEYS.map((l) => [l, overlay(base[l], src[l])])) : base);
      setMeta(data);
    }).catch(() => { setDraft(buildBase()); toast.error('تعذر تحميل المحتوى، تم استخدام القيم الافتراضية'); });
    loadRevisions().catch(() => {});
  }, [loadRevisions]);

  // value getters/setters ------------------------------------------------
  const get = useCallback((lang, path) => getAt(draft?.[lang], path), [draft]);

  const set = useCallback((lang, path, value) => {
    setDraft((d) => ({ ...d, [lang]: setAt(d[lang], path, value) }));
    setDirty(true);
  }, []);

  const setShared = useCallback((path, value) => {
    setDraft((d) => { const n = { ...d }; LANG_KEYS.forEach((l) => { n[l] = setAt(n[l], path, value); }); return n; });
    setDirty(true);
  }, []);

  const listOp = useCallback((path, op) => {
    setDraft((d) => {
      const n = { ...d };
      LANG_KEYS.forEach((l) => {
        const arr = [...(getAt(n[l], path) || [])];
        n[l] = setAt(n[l], path, op(arr, l));
      });
      return n;
    });
    setDirty(true);
  }, []);

  const addItem = (path, template = {}) => listOp(path, (arr) => [...arr, { visible: true, id: `item-${Date.now().toString(36)}`, ...template }]);
  const removeItem = (path, i) => listOp(path, (arr) => arr.filter((_, idx) => idx !== i));
  const moveItem = (path, i, dir) => listOp(path, (arr) => {
    const j = i + dir;
    if (j < 0 || j >= arr.length) return arr;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    return arr;
  });

  // server ops -------------------------------------------------------------
  const save = async (note = '') => {
    setBusy(true);
    try {
      const { data } = await axios.put(`${API}/admin/content/draft`, { data: draft, note }, authHeaders());
      setMeta((m) => ({ ...m, updated_at: data.updated_at, updated_by: user.email }));
      setDirty(false);
      toast.success('تم حفظ المسودة');
      return true;
    } catch (e) { toast.error(apiError(e)); return false; } finally { setBusy(false); }
  };

  const publish = async () => {
    setBusy(true);
    try {
      if (dirty) await axios.put(`${API}/admin/content/draft`, { data: draft, note: 'auto-save before publish' }, authHeaders());
      const { data } = await axios.post(`${API}/admin/content/publish`, {}, authHeaders());
      setMeta((m) => ({ ...m, published_at: data.published_at, published_by: user.email, published: draft }));
      setDirty(false);
      toast.success('تم النشر — التغييرات ظاهرة الآن للزوار');
      loadRevisions().catch(() => {});
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const discard = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/content/discard`, {}, authHeaders());
      setDraft(data.draft || buildBase());
      setDirty(false);
      toast.success('تم التراجع إلى النسخة المنشورة');
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const restore = async (rid) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/content/revisions/${rid}/restore`, {}, authHeaders());
      setDraft(data.draft);
      setDirty(false);
      toast.success('تم استرجاع النسخة إلى المسودة — انشرها لتظهر للزوار');
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const resetDefaults = () => { setDraft(buildBase()); setDirty(true); toast.info('تمت إعادة المسودة إلى المحتوى الافتراضي (لم يُحفظ بعد)'); };

  const openPreview = async () => {
    if (dirty) { const ok = await save('preview'); if (!ok) return; }
    window.open('/?preview=1', '_blank');
  };

  const value = useMemo(() => ({ draft, meta, dirty, busy, revisions, get, set, setShared, addItem, removeItem, moveItem, save, publish, discard, restore, resetDefaults, openPreview, user }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [draft, meta, dirty, busy, revisions, user]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useAdminContent = () => useContext(Ctx);
